import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SITE } from "@/config/site";

const PRIVACY = [
  {
    h: "Introduction",
    p: [
      "Postings Inc. respects your privacy and is committed to protecting the personal information you share with us. This policy describes the types of personal information we collect on this website and how we use it.",
    ],
  },
  {
    h: "Collecting personal information",
    p: [
      "We collect personal information that you provide through our contact form, such as your name, business name, email address, phone number, province, current accounting software, number of employees, the services you are interested in and any message you include.",
      "Like most websites, we may also collect basic technical information when you visit, such as your browser type and the pages you view.",
    ],
  },
  {
    h: "Using your personal information",
    p: [
      "We use the information you submit for one primary purpose: responding to your inquiry and connecting you with the right member of the Postings team.",
      "We will not enrol you in promotional marketing as a result of submitting a contact-form inquiry, and we do not sell or rent your personal information to third parties.",
    ],
  },
  {
    h: "Client information",
    p: [
      "Information related to bookkeeping and payroll engagements is handled under the terms of your service agreement. Our secure client applications, Client Manager and PostWage Payroll, are separate systems with their own access controls.",
    ],
  },
  {
    h: "Disclosing personal information",
    p: [
      "We disclose personal information only to those members of our team who need it to respond to you, and where required by law. We do not otherwise share your information for marketing purposes.",
    ],
  },
  {
    h: "Securing and retaining your information",
    p: [
      "We store inquiries securely and retain them only as long as needed to respond to your request and maintain our business records.",
    ],
  },
  {
    h: "Your rights",
    p: [
      "You may ask whether we hold personal information about you, request a copy, or ask us to correct or remove it. Contact us using the details below and we will respond promptly.",
    ],
  },
  {
    h: "Updating this policy",
    p: [
      "We may update this policy from time to time. The current version is always available on this page.",
    ],
  },
  {
    h: "Contact us",
    p: [
      `Postings Inc. — ${SITE.address.street}, ${SITE.address.city}, ${SITE.address.province}. Phone: ${SITE.phoneDisplay}. Email: ${SITE.email}.`,
    ],
  },
];

const TERMS = [
  {
    h: "Introduction",
    p: [
      "These terms of use govern your use of our website. By using our website, you accept these terms in full. If you disagree with these terms or any part of these terms, you must not use our website.",
    ],
  },
  {
    h: "License to use website",
    p: [
      "Unless otherwise stated, Postings Inc. and/or its licensors own the intellectual property rights published on this website and materials used on Postings Inc. Subject to the license below, all these intellectual property rights are reserved.",
      "You may view, download for caching purposes only, and print pages from the website for your own personal use, subject to the restrictions set out below and elsewhere in these terms.",
    ],
  },
  {
    h: "Acceptable use",
    p: [
      "You must not use this website in any way that causes, or may cause, damage to the website or impairment of the availability or accessibility of the website; or in any way which is unlawful, illegal, fraudulent or harmful, or in connection with any unlawful, illegal, fraudulent or harmful purpose or activity.",
    ],
  },
  {
    h: "Restricted access",
    p: [
      "Access to certain areas of this website is restricted. Postings Inc. reserves the right to restrict access to areas of this website, or indeed this entire website, at Postings Inc.'s discretion.",
    ],
  },
  {
    h: "User content",
    p: [
      "In these terms of use, \u201cyour user content\u201d means material (including without limitation text, images, audio material, video material and audio-visual material) that you submit to this website, for whatever purpose.",
      "You grant to Postings Inc. a worldwide, irrevocable, non-exclusive, royalty-free license to use, reproduce, adapt, publish, translate and distribute your user content in any existing or future media. You also grant to Postings Inc. the right to sub-license these rights, and the right to bring an action for infringement of these rights.",
    ],
  },
  {
    h: "No warranties",
    p: [
      "This website is provided \u201cas is\u201d without any representations or warranties, express or implied. Postings Inc. makes no representations or warranties in relation to this website or the information and materials provided on this website.",
    ],
  },
  {
    h: "Limitations of liability",
    p: [
      "Postings Inc. will not be liable to you in relation to the contents of, or use of, or otherwise in connection with, this website:",
      "1. for any indirect, special or consequential loss; or",
      "2. for any business losses, loss of revenue, income, profits or anticipated savings; or",
      "3. for any loss or corruption of any data, database or software; or",
      "4. for any direct loss.",
    ],
  },
  {
    h: "Indemnity",
    p: [
      "You hereby indemnify Postings Inc. and undertake to keep Postings Inc. indemnified against any losses, damages, costs, liabilities and expenses (including without limitation legal expenses and any amounts paid by Postings Inc. to a third party in settlement of a claim or dispute on the advice of Postings Inc.'s legal advisers) incurred or suffered by Postings Inc. arising out of any breach by you of any provision of these terms of use.",
    ],
  },
  {
    h: "Breaches of these terms",
    p: [
      "Without prejudice to Postings Inc.'s other rights under these terms of use, if you breach these terms in any way, Postings Inc. may take such action as Postings Inc. deems appropriate to deal with the breach, including suspending your access to the website, prohibiting you from accessing the website, blocking computers using your IP address from accessing the website, contacting your internet service provider to request that they block your access to the website and/or bringing court proceedings against you.",
    ],
  },
  {
    h: "Variation",
    p: [
      "Postings Inc. may revise these terms of use from time to time. Revised terms will apply to the use of this website from the date of the publication of the revised terms on this website. Please check this page regularly to ensure you are familiar with the current version.",
    ],
  },
  {
    h: "Assignment",
    p: [
      "Postings Inc. may transfer, sub-contract or otherwise deal with Postings Inc.'s rights and/or obligations under these terms without notifying you or obtaining your consent.",
    ],
  },
  {
    h: "Entire agreement",
    p: [
      "These terms constitute the entire agreement between you and Postings Inc. in relation to your use of this website and supersede all previous agreements in respect of your use of this website.",
    ],
  },
  {
    h: "Law and jurisdiction",
    p: [
      "These terms will be governed by and construed in accordance with the laws of Ontario, and any disputes relating to these terms will be subject to the exclusive jurisdiction of the courts of Ontario.",
    ],
  },
];

const INTROS = {
  privacy:
    "How Postings Inc. collects, uses and protects the information you share through this website.",
  terms: "The terms that govern your use of the Postings Inc. website.",
};

const DOCS = { privacy: PRIVACY, terms: TERMS };

export default function PrivacyDialog({ open, doc = "privacy", onOpenChange }) {
  const sections = DOCS[doc] || PRIVACY;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto rounded-2xl" data-testid="legal-dialog">
        <DialogHeader>
          <DialogTitle className="font-cabinet text-2xl font-bold">
            {doc === "terms" ? "Website Terms of Use" : "Privacy Policy"}
          </DialogTitle>
          <DialogDescription>{INTROS[doc] || INTROS.privacy}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5" data-testid={`legal-content-${doc}`}>
          {sections.map((s) => (
            <div key={s.h}>
              <h4 className="text-sm font-bold text-ink">{s.h}</h4>
              {s.p.map((para, i) => (
                <p key={i} className="mt-1.5 text-sm leading-relaxed text-slate-600">
                  {para}
                </p>
              ))}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
